import type { QueryClient } from '@tanstack/react-query';

import type { useOrpc } from '@/_shared/hooks/useOrpc';

type Orpc = ReturnType<typeof useOrpc>;

/**
 * How long after a turn ends to read a still-missing auto-title back once more. The titler gives its
 * Haiku call 10 s and one retry (`src/agent/chat-titler.ts`), so a name can land about 20 s after the
 * turn started. Not imported from there: that module pulls the model SDK, which must not reach the SPA.
 */
export const TITLE_READBACK_RETRY_MS = 22_000;

/**
 * Write a chat's new name into every cache that renders it: the conversation header reads
 * `chat.get`, the Chats page and the sidebar Recent chats read `chat.list`.
 *
 * A patch, not a refetch, so only a caller holding an AUTHORITATIVE name may use it: the rename
 * mutation's own response, or the live `chat_title` event. A persisted `change_title` message is not
 * authoritative — it replays on every load — so it goes through `refetchChatTitle` instead.
 */
export function applyChatTitle(queryClient: QueryClient, orpc: Orpc, id: string, title: string): void {
  queryClient.setQueryData(orpc.chat.get.queryOptions({ input: { id } }).queryKey, (previous) =>
    previous ? { ...previous, session: { ...previous.session, title } } : previous,
  );
  queryClient.setQueryData(orpc.chat.list.queryOptions({ input: {} }).queryKey, (previous) =>
    previous ? { ...previous, sessions: previous.sessions.map((s) => (s.id === id ? { ...s, title } : s)) } : previous,
  );
}

/** Re-read a chat's name from the server, for a caller that knows a name changed but not what to. */
export function refetchChatTitle(queryClient: QueryClient, orpc: Orpc, id: string): void {
  void queryClient.invalidateQueries({ queryKey: orpc.chat.get.queryOptions({ input: { id } }).queryKey });
  void queryClient.invalidateQueries({ queryKey: orpc.chat.list.queryOptions({ input: {} }).queryKey });
}

/** Cancel a pending delayed title read-back, if one is scheduled. */
export function clearTitleRetry(timer: { current: ReturnType<typeof setTimeout> | null }): void {
  if (timer.current !== null) clearTimeout(timer.current);
  timer.current = null;
}

/**
 * The rename was refused as not-found. From the UI that means the chat was deleted meanwhile (in
 * another tab, or by its owner elsewhere): the menus never offer a rename the server would refuse on
 * ownership or on an artifact-bound chat. A retry cannot succeed, so the dialog closes instead.
 */
export function isChatGone(err: unknown): boolean {
  return (err as { code?: unknown } | null)?.code === 'NOT_FOUND';
}
